import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  BookOpen,
  Filter,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Star,
  Bookmark,
  Hash,
  Users,
  Calendar,
  Eye,
  Plus,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "react-toastify";
import { server } from "../server";

const ChaptersList = () => {
  const [chapters, setChapters] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredChapters, setFilteredChapters] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);

  // Fetch subjects and chapters
  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Fetch subjects
      const subjectsResponse = await axios.get(`${server}/subject/all`, {
        params: { pageNo: 1, pageSize: 100 },
      });
      setSubjects(subjectsResponse.data?.data || []);

      // Fetch chapters
      const chaptersResponse = await axios.get(`${server}/chapter/all`, {
        params: { pageNo: 1, pageSize: 100 },
      });
      setChapters(chaptersResponse.data?.data || []);
      setFilteredChapters(chaptersResponse.data?.data || []);
    } catch (error) {
      toast.error("Failed to load data");
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter and sort chapters
  useEffect(() => {
    let result = [...chapters];

    // Filter by subject
    if (selectedSubject) {
      result = result.filter(
        (chapter) => chapter.subjectId === parseInt(selectedSubject)
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (chapter) =>
          chapter.chapter?.toLowerCase().includes(term) ||
          getSubjectName(chapter.subjectId)?.toLowerCase().includes(term)
      );
    }

    // Sort chapters
    result.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = a.chapter?.toLowerCase();
          bValue = b.chapter?.toLowerCase();
          break;
        case "subject":
          aValue = getSubjectName(a.subjectId)?.toLowerCase();
          bValue = getSubjectName(b.subjectId)?.toLowerCase();
          break;
        case "popular":
          aValue = a.isPopular ? 1 : 0;
          bValue = b.isPopular ? 1 : 0;
          break;
        default:
          aValue = a.chapter?.toLowerCase();
          bValue = b.chapter?.toLowerCase();
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredChapters(result);
  }, [chapters, selectedSubject, searchTerm, sortBy, sortOrder]);

  const getSubjectName = (subjectId) => {
    const subject = subjects.find((sub) => sub.id === subjectId);
    return subject ? subject.subject : "Unknown Subject";
  };

  const getSubjectIcon = (subjectId) => {
    const subject = subjects.find((sub) => sub.id === subjectId);
    return subject ? subject.icon : "📚";
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const toggleChapterExpand = (chapterId) => {
    setExpandedChapter(expandedChapter === chapterId ? null : chapterId);
  };

  const toggleMenu = (chapterId) => {
    setActiveMenu(activeMenu === chapterId ? null : chapterId);
  };

  const handleEdit = (chapter) => {
    toast.info(`Edit chapter: ${chapter.chapter}`);
    setActiveMenu(null);
  };

  const handleDelete = (chapter) => {
    if (
      window.confirm(`Are you sure you want to delete "${chapter.chapter}"?`)
    ) {
      toast.success(`Chapter "${chapter.chapter}" deleted`);
      setActiveMenu(null);
    }
  };

  const handleViewDetails = (chapter) => {
    toast.info(`View details: ${chapter.chapter}`);
    setActiveMenu(null);
  };

  const clearFilters = () => {
    setSelectedSubject("");
    setSearchTerm("");
    setSortBy("name");
    setSortOrder("asc");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl shadow-lg">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Chapters Library</h1>
          </div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Browse and manage all chapters across different subjects. Filter by
            subject, search by name, and sort as needed.
          </p>
        </motion.div>

        {/* Filters and Controls */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-6 shadow-2xl border border-gray-700 mb-8"
          variants={itemVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <Search className="w-4 h-4 text-blue-400" />
                Search Chapters
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by chapter name or subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 pl-10 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>

            {/* Subject Filter */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <Filter className="w-4 h-4 text-green-400" />
                Filter by Subject
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-200"
              >
                <option value="">All Subjects</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.icon} {subject.subject}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <ChevronUp className="w-4 h-4 text-purple-400" />
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
              >
                <option value="name">Chapter Name</option>
                <option value="subject">Subject</option>
                <option value="popular">Popularity</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-6 pt-6 border-t border-gray-700">
            <div className="flex items-center gap-4">
              <motion.button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-700 border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-600 transition-all duration-200 flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <RefreshCw className="w-4 h-4" />
                Clear Filters
              </motion.button>

              <div className="text-sm text-gray-400">
                Showing {filteredChapters.length} of {chapters.length} chapters
              </div>
            </div>

            <motion.button
              onClick={() => toast.info("Add new chapter feature")}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus className="w-4 h-4" />
              Add New Chapter
            </motion.button>
          </div>
        </motion.div>

        {/* Chapters Grid */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <AnimatePresence>
            {isLoading ? (
              // Loading Skeleton
              Array.from({ length: 6 }).map((_, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-800 rounded-2xl p-6 border border-gray-700 animate-pulse"
                  variants={itemVariants}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-6 bg-gray-700 rounded w-3/4"></div>
                    <div className="h-8 w-8 bg-gray-700 rounded"></div>
                  </div>
                  <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-700 rounded"></div>
                    <div className="h-3 bg-gray-700 rounded w-5/6"></div>
                  </div>
                </motion.div>
              ))
            ) : filteredChapters.length === 0 ? (
              // Empty State
              <motion.div
                className="col-span-full text-center py-16 bg-gray-800 rounded-2xl border border-gray-700"
                variants={itemVariants}
              >
                <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-300 mb-2">
                  No chapters found
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {searchTerm || selectedSubject
                    ? "Try adjusting your search criteria or filters to find what you're looking for."
                    : "No chapters have been added yet. Start by adding your first chapter!"}
                </p>
              </motion.div>
            ) : (
              // Chapters Cards
              filteredChapters.map((chapter, index) => (
                <motion.div
                  key={chapter.id}
                  layout
                  variants={itemVariants}
                  className="bg-gray-800 rounded-2xl border border-gray-700 hover:border-purple-500/30 transition-all duration-300 hover:shadow-2xl group"
                  whileHover={{ y: -5 }}
                >
                  {/* Card Header */}
                  <div className="p-6 border-b border-gray-700">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
                          <span className="text-lg">
                            {getSubjectIcon(chapter.subjectId)}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors duration-200 line-clamp-2">
                            {chapter.chapter}
                          </h3>
                          <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                            <Bookmark className="w-3 h-3" />
                            {getSubjectName(chapter.subjectId)}
                          </p>
                        </div>
                      </div>

                      {/* Menu Button */}
                      <div className="relative">
                        <motion.button
                          onClick={() => toggleMenu(chapter.id)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all duration-200"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </motion.button>

                        {/* Dropdown Menu */}
                        <AnimatePresence>
                          {activeMenu === chapter.id && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: -10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: -10 }}
                              className="absolute right-0 top-full mt-1 w-48 bg-gray-700 border border-gray-600 rounded-xl shadow-2xl z-10"
                            >
                              <button
                                onClick={() => handleViewDetails(chapter)}
                                className="w-full px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-600 flex items-center gap-2 rounded-t-xl"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              <button
                                onClick={() => handleEdit(chapter)}
                                className="w-full px-4 py-3 text-left text-sm text-gray-300 hover:bg-gray-600 flex items-center gap-2"
                              >
                                <Edit className="w-4 h-4" />
                                Edit Chapter
                              </button>
                              <button
                                onClick={() => handleDelete(chapter)}
                                className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2 rounded-b-xl"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete Chapter
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Popular Badge */}
                    {chapter.isPopular && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500/20 text-orange-300 text-xs rounded-full border border-orange-500/30"
                      >
                        <Star className="w-3 h-3 fill-current" />
                        Popular Chapter
                      </motion.div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      <div className="text-center p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                        <Hash className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                        <div className="text-white font-semibold">
                          Chapter ID
                        </div>
                        <div className="text-gray-400 text-xs">
                          {chapter.id}
                        </div>
                      </div>
                      <div className="text-center p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                        <Users className="w-4 h-4 text-green-400 mx-auto mb-1" />
                        <div className="text-white font-semibold">
                          Subject ID
                        </div>
                        <div className="text-gray-400 text-xs">
                          {chapter.subjectId}
                        </div>
                      </div>
                    </div>

                    {/* Expandable Section */}
                    <motion.button
                      onClick={() => toggleChapterExpand(chapter.id)}
                      className="w-full flex items-center justify-between p-3 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-all duration-200"
                    >
                      <span className="text-sm font-medium">Quick Actions</span>
                      {expandedChapter === chapter.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </motion.button>

                    <AnimatePresence>
                      {expandedChapter === chapter.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-3 pt-3 border-t border-gray-700 space-y-2">
                            <button
                              onClick={() => handleViewDetails(chapter)}
                              className="w-full text-left px-3 py-2 text-sm text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors duration-200"
                            >
                              📚 View Topic Details
                            </button>
                            <button
                              onClick={() => handleEdit(chapter)}
                              className="w-full text-left px-3 py-2 text-sm text-green-400 hover:bg-green-500/10 rounded-lg transition-colors duration-200"
                            >
                              ✏️ Edit Chapter Info
                            </button>
                            <button
                              onClick={() =>
                                toast.info(`Share: ${chapter.chapter}`)
                              }
                              className="w-full text-left px-3 py-2 text-sm text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors duration-200"
                            >
                              🔗 Share Chapter
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default ChaptersList;
